/**
 * Rendered mode of a section (spec §8.19): the document read from Loro, serialised to DOM by the
 * schema — no editor, no markdown renderer, no sanitiser (only schema `toDOM` output is produced).
 * Links open in a new tab and only http(s)/mailto become links. Rebuilt when the container changes.
 */
import { createNodeFromLoroObj, type LoroNode } from "loro-prosemirror";
import { LoroMap, type ContainerID, type LoroDoc } from "loro-crdt";
import { DOMSerializer, type Node } from "prosemirror-model";
import { safeHref } from "./markdown";
import { pmSchema } from "./pmSchema";

const serializer = new DOMSerializer(DOMSerializer.nodesFromSchema(pmSchema), {
  ...DOMSerializer.marksFromSchema(pmSchema),
  link: (mark) => {
    const href = safeHref(mark.attrs.href);
    return href ? ["a", { href, target: "_blank", rel: "noopener noreferrer", title: mark.attrs.title ?? undefined }, 0] : ["span", 0];
  },
});

/** The section's current ProseMirror document, read from its Loro container (empty if never edited). */
export function docFromLoro(doc: LoroDoc, containerId: ContainerID): Node {
  const obj = doc.getContainerById(containerId);
  if (obj instanceof LoroMap && typeof obj.get("nodeName") === "string") {
    try {
      return createNodeFromLoroObj(pmSchema, obj as unknown as LoroNode, new Map());
    } catch {
      /* a malformed container renders as empty */
    }
  }
  return pmSchema.node("doc", null, [pmSchema.node("paragraph")]);
}

export function renderInto(el: HTMLElement, node: Node, emptyText: string): void {
  el.replaceChildren(serializer.serializeFragment(node.content));
  if (!node.textContent.trim() && !hasLeaf(node)) {
    const empty = document.createElement("p");
    empty.className = "text-neutral-400";
    empty.textContent = emptyText;
    el.replaceChildren(empty);
  }
}

function hasLeaf(node: Node): boolean {
  let found = false;
  node.descendants((n) => {
    if (n.type.name === "horizontal_rule") found = true;
    return !found;
  });
  return found;
}

/** Path-prefix test of §8.18: does this event batch touch the container? */
export function touches(doc: LoroDoc, containerId: ContainerID): (events: Array<{ path: unknown[] }>) => boolean {
  const path = doc.getPathToContainer(containerId) ?? [];
  return (events) => events.some((e) => path.length <= e.path.length && path.every((p, i) => e.path[i] === p));
}

/** `emptyText` is what an empty section says, in the reader's language (spec §8.23). */
export function mountRendered(parent: HTMLElement, opts: { doc: LoroDoc; containerId: ContainerID; emptyText: string }): { destroy(): void } {
  const el = document.createElement("div");
  el.className = "ProseMirror-rendered";
  el.dataset.testid = "section-rendered";
  parent.appendChild(el);
  const render = () => renderInto(el, docFromLoro(opts.doc, opts.containerId), opts.emptyText);
  render();
  const hit = touches(opts.doc, opts.containerId);
  let queued = false;
  const unsub = opts.doc.subscribe((batch) => {
    if (queued || !hit(batch.events)) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      render();
    });
  });
  return {
    destroy() {
      unsub();
      el.remove();
    },
  };
}
